import { ForemanDB } from '../../types/IndexedDB/ForemanDB'
import { MutationSnapshot, ChangeType } from '../../types/Cache/MutationSnapshot'

export class MutationSnapshotService {
	private db: ForemanDB
	private currentSnapshot: MutationSnapshot | null = null

	constructor(db: ForemanDB) {
		this.db = db
	}

	/**
	 * Initialize or load the current mutation snapshot
	 */
	async initializeSnapshot(userId: number): Promise<void> {
		try {
			// Try to load existing snapshot
			const existingSnapshot = await this.db.mutationSnapshot.where('userId').equals(userId).first()

			if (existingSnapshot) {
				this.currentSnapshot = existingSnapshot
			} else {
				// Create new snapshot
				this.currentSnapshot = {
					userId,
					// createdAt: new Date(),
					directReports: [],
					workItems: [],
					tasks: [],
					reworks: [],
					constraints: [],
					requests: [],
					workItemAssignments: [],
					taskAssignments: [],
					reworkAssignments: [],
					qcWorkers: [],
					attendance: [],
					pallets: [],
					palletItems: [],
					timesheets: [],
					timeRecords: [],
					progressRecords: [],
					attachments: [],
					consentForms: [],
					updatedAttachments: [],
				}

				await this.saveSnapshot()
			}
		} catch (error) {
			console.error('Error initializing mutation snapshot:', error)
			throw error
		}
	}

	/**
	 * Get the current snapshot for synchronization
	 */
	async getSnapshotForSync(): Promise<MutationSnapshot | null> {
		if (!this.currentSnapshot) {
			return null
		}

		// Filter out unchanged entities to reduce payload size
		const filteredTimesheets = this.currentSnapshot.timesheets.filter(
			(c) => c.changeType !== ChangeType.UNCHANGED,
		)
		const filteredTimeRecords = this.currentSnapshot.timeRecords.filter(
			(c) => c.changeType !== ChangeType.UNCHANGED,
		)

		// Get IDs of locally created timesheets (INSERTED)
		const locallyCreatedTimesheetIds = new Set(
			filteredTimesheets
				.filter((ts) => ts.changeType === ChangeType.INSERTED)
				.map((ts) => ts.entity.id),
		)

		// Keep timeRecords that don't have their timesheet in the snapshot
		const orphanedTimeRecords = filteredTimeRecords.filter(
			(tr) => !locallyCreatedTimesheetIds.has(tr.entity.timesheetId || 0),
		)


		// Correlate timeRecords with their timesheets
		const timesheetsWithActivities = filteredTimesheets.map((timesheetChange) => {
			const timesheet = { ...timesheetChange.entity }

			// Only add activities for locally created timesheets
			if (timesheetChange.changeType === ChangeType.INSERTED) {
				const correlatedTimeRecords = filteredTimeRecords
					.filter((tr) => tr.entity.timesheetId === timesheet.id)
					.map((tr) => tr.entity)

				timesheet.activities = correlatedTimeRecords
			}

			return {
				...timesheetChange,
				entity: timesheet,
			}
		})

		const filteredSnapshot: MutationSnapshot = {
			...this.currentSnapshot,
			directReports: this.currentSnapshot.directReports.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			workItems: this.currentSnapshot.workItems.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			tasks: this.currentSnapshot.tasks.filter((c) => c.changeType !== ChangeType.UNCHANGED),
			reworks: this.currentSnapshot.reworks.filter((c) => c.changeType !== ChangeType.UNCHANGED),
			constraints: this.currentSnapshot.constraints.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			requests: this.currentSnapshot.requests.filter((c) => c.changeType !== ChangeType.UNCHANGED),
			workItemAssignments: this.currentSnapshot.workItemAssignments.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			taskAssignments: this.currentSnapshot.taskAssignments.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			reworkAssignments: this.currentSnapshot.reworkAssignments.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			qcWorkers: this.currentSnapshot.qcWorkers.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			attendance: this.currentSnapshot.attendance.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			pallets: this.currentSnapshot.pallets.filter((c) => c.changeType !== ChangeType.UNCHANGED),
			palletItems: this.currentSnapshot.palletItems.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			timesheets: timesheetsWithActivities,
			timeRecords: orphanedTimeRecords,
			progressRecords: this.currentSnapshot.progressRecords.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			attachments: this.currentSnapshot.attachments.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
			updatedAttachments: this.currentSnapshot.updatedAttachments.filter(
				(c) => c.changeType !== ChangeType.UNCHANGED,
			),
		}

		// Only return if there are actual changes
		const hasChanges = Object.values(filteredSnapshot).some((value) => {
			if (Array.isArray(value)) {
				return value.length > 0
			}
			return value && typeof value === 'object' && 'changeType' in value
		})

		return hasChanges ? filteredSnapshot : null
	}

	/**
	 * Clear all synced records from the snapshot
	 */
	private clearSyncedRecords(): void {
		if (!this.currentSnapshot) return

		// Clear all arrays to remove synced records
		this.currentSnapshot.directReports = []
		this.currentSnapshot.workItems = []
		this.currentSnapshot.tasks = []
		this.currentSnapshot.reworks = []
		this.currentSnapshot.constraints = []
		this.currentSnapshot.requests = []
		this.currentSnapshot.workItemAssignments = []
		this.currentSnapshot.taskAssignments = []
		this.currentSnapshot.reworkAssignments = []
		this.currentSnapshot.qcWorkers = []
		this.currentSnapshot.attendance = []
		this.currentSnapshot.pallets = []
		this.currentSnapshot.palletItems = []
		this.currentSnapshot.timesheets = []
		this.currentSnapshot.timeRecords = []
		this.currentSnapshot.progressRecords = []
		this.currentSnapshot.attachments = []
		this.currentSnapshot.updatedAttachments = []
	}

	/**
	 * Mark snapshot as synced
	 */
	async markAsSynced(): Promise<void> {
		if (!this.currentSnapshot) {
			return
		}

		// Clear all synced records from the snapshot
		this.clearSyncedRecords()

		await this.saveSnapshot()
	}

	/**
	 * Save snapshot to database
	 */
	private async saveSnapshot(): Promise<void> {
		if (!this.currentSnapshot) return

		try {
			await this.db.mutationSnapshot.put(this.currentSnapshot)
		} catch (error) {
			console.error('Error saving mutation snapshot:', error)
			throw error
		}
	}

	/**
	 * Clear the snapshot (useful for testing or reset)
	 */
	async clearSnapshot(): Promise<void> {
		if (!this.currentSnapshot) return

		try {
			await this.db.mutationSnapshot.delete(this.currentSnapshot.userId!)
			this.currentSnapshot = null
		} catch (error) {
			console.error('Error clearing mutation snapshot:', error)
			throw error
		}
	}
}
